import { ApplicationElement, html } from '@effectivastudio/lit-components';
import { ref, createRef } from 'lit/directives/ref.js';
import { keyed } from 'lit/directives/keyed.js';
import { execJSFromResponse } from '@effectivastudio/lit-components/lib/helpers.js';

import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

import translations from './translations.js';
import style from './style.lit.css';

import '@effectivastudio/lit-components/components/icon/element.js';
import '@effectivastudio/lit-components/components/icon_block/element.js';
import '@effectivastudio/lit-components/components/hero_2/element.js';
import '@effectivastudio/lit-components/components/grid_list/element.js';

/**
 * Booking calendar
 *
 * @prop dataUrl - URL to fetch slots from (JSON)
 * @prop cardId - ID of the booking card
 * @prop businessHours - Business hours for the calendar (see fullcalendar docs)
 * @prop initialView - Initial view of the calendar (see fullcalendar docs)
 * @prop initialDate - Initial date of the calendar (see fullcalendar docs)
 * @prop locale - Locale of the calendar (see fullcalendar docs) (options: en, de, it)
 * @prop isMobile - Indicates if the calendar is displayed on mobile
 *
 */

export default class BookingCalendar extends ApplicationElement {
  /** @internal */
  static translations = translations;
  static styles = [ApplicationElement.styles, style];
  static properties = {
    dataUrl: { type: String },
    cardId: { type: Number },
    businessHours: { type: Array },
    initialView: { type: String },
    initialDate: { type: Date },
    locale: { type: String },
    viewTitle: { type: String, state: true },
    isMobile: { type: Boolean }, // not in use yet
    bookingSlotsCount: { state: true },
    fragmentKey: { state: true },
  };

  /** @internal */
  iconsMap = {
    dayGridMonth: 'calendar-alt',
    timeGridWeek: 'calendar-lines',
    timeGridDay: 'calendar-day',
    listWeek: 'list-alt',
  };

  /** @internal */
  calendarRef = createRef();

  constructor() {
    super();
    this.initialView = 'dayGridMonth';
    this.locale = 'en';
    this.fragmentKey = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('card-booking-modal-updated', this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('card-booking-modal-updated', this);
  }

  firstUpdated() {
    super.firstUpdated();
    this.initializeCalendar();
  }

  handleEvent(e) {
    if (e.type === 'card-booking-modal-updated') {
      this.reload();
    }
  }

  handleBookingSlotRemove() {
    this.bookingSlotsCount -= 1;
    this.reload(false);
  }

  handleBookingSlotUpdate() {
    this.reload(false);
  }

  /** @internal */
  get fragmentUrl() {
    return `/booking_cards/${this.cardId}/calendar.fragment`;
  }

  /** @internal */
  get calendar() {
    return this.calendarRef.value;
  }

  /** @internal */
  get viewTitleIcon() {
    return this.iconsMap[this.fullCalendar?.view.type];
  }

  /** @internal */
  get isToday() {
    return !!this.fullCalendar?.el.querySelector('.fc-day-today');
  }

  showEventUrl(info) {
    return `/booking_cards/${this.cardId}/slots/${info.event.id}`;
  }

  newEventUrl(info) {
    const params = {
      starts_at: info.startStr,
      ends_at: info.endStr,
      all_day: info.allDay,
    };

    const base = `/booking_cards/${this.cardId}/slots/new`;
    const url = new URL(base, window.location.origin);
    url.search = new URLSearchParams(params);

    return url.toString();
  }

  /**
   * Helper function to simplify testing
   * @internal
   * */
  /* c8 ignore next 3 */
  execJSFromResponse(url) {
    return execJSFromResponse(url);
  }

  onSelect(info) {
    this.execJSFromResponse(this.newEventUrl(info));
  }

  onEventClick(info) {
    this.execJSFromResponse(this.showEventUrl(info));
  }

  initializeCalendar() {
    this.fullCalendar = new Calendar(this.calendar, {
      events: this.dataUrl,
      initialView: this.initialView,
      initialDate: this.initialDate,
      businessHours: this.businessHours,
      locale: this.locale,
      height: 'var(--calendar-height)',
      plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin],
      headerToolbar: false,
      nowIndicator: true,
      selectable: true,
      selectMirror: true,
      slotDuration: '00:15:00',
      firstDay: 1,
      select: this.onSelect.bind(this),
      eventClick: this.onEventClick.bind(this),
      datesSet: () => {
        this.viewTitle = this.fullCalendar.view.title;
      },
    });

    this.fullCalendar.render();
  }

  reload(fragmentUpdate = true) {
    this.fullCalendar.refetchEvents();
    fragmentUpdate && (this.fragmentKey += 1);
  }

  onMonth() {
    this.fullCalendar.changeView('dayGridMonth');
  }

  onDay() {
    this.fullCalendar.changeView('timeGridDay');
  }

  onWeek() {
    this.fullCalendar.changeView('timeGridWeek');
  }

  onList() {
    this.fullCalendar.changeView('listWeek');
  }

  onPrev() {
    this.fullCalendar.prev();
  }

  onNext() {
    this.fullCalendar.next();
  }

  onToday() {
    this.fullCalendar.today();
  }

  activeView(name) {
    return this.fullCalendar?.view.type === name ? 'active' : '';
  }

  renderPrevNext() {
    return html`
      <ef-grid-list class="nav" col="0" gap="small">
        <button class="btn-v1 btn-sm" @click=${this.onPrev}>
          <ef-icon name="chevron-left"></ef-icon>
        </button>
        <button
          class="btn-v1 btn-sm ${this.isToday ? 'active' : null} today"
          @click=${this.onToday}>
          ${this.t('buttons.today')}
        </button>
        <button class="btn-v1 btn-sm" @click=${this.onNext}>
          <ef-icon name="chevron-right"></ef-icon>
        </button>
      </ef-grid-list>
    `;
  }

  renderViewButtons() {
    return html`
      <ef-grid-list col="4" colsm="0" gap="small">
        <button
          class="btn-v1 btn-sm ${this.activeView('dayGridMonth')}"
          @click=${this.onMonth}>
          <ef-icon-block icon="calendar-alt" text="${this.t('buttons.month')}">
          </ef-icon-block>
        </button>

        <button
          class="btn-v1 btn-sm ${this.activeView('timeGridWeek')}"
          @click=${this.onWeek}>
          <ef-icon-block icon="calendar-lines" text="${this.t('buttons.week')}">
          </ef-icon-block>
        </button>

        <button
          class="btn-v1 btn-sm ${this.activeView('timeGridDay')}"
          @click=${this.onDay}>
          <ef-icon-block icon="calendar-day" text="${this.t('buttons.day')}">
          </ef-icon-block>
        </button>

        <button
          class="btn-v1 btn-sm ${this.activeView('listWeek')}"
          @click=${this.onList}>
          <ef-icon-block icon="list-alt" text="${this.t('buttons.list')}">
          </ef-icon-block>
        </button>
      </ef-grid-list>
    `;
  }

  renderHeader() {
    return html`
      <header>
        ${this.renderPrevNext()}

        <ef-icon-block
          class="title"
          icon="${this.viewTitleIcon}"
          text="${this.viewTitle}"
          theme="primary"></ef-icon-block>

        ${this.renderViewButtons()}
      </header>
    `;
  }

  renderHero() {
    return html`
      <ef-hero-2 icon="calendar-day">
        <h1 slot="title">${this.t('my_reservations.title')}</h1>
        <p slot="short">${this.t('my_reservations.desc')}</p>
      </ef-hero-2>
    `;
  }

  renderLabel() {
    return html`
      <ef-icon-block icon="calendar-day">
        ${this.t('my_reservations.title')}
      </ef-icon-block>
    `;
  }

  handleHero() {
    this.bookingSlotsCount =
      this.shadowRoot.querySelectorAll('bu-booking-slot').length;
  }

  render() {
    return html`
      <div class="calendar">
        ${this.renderHeader()}
        <main ${ref(this.calendarRef)}></main>
      </div>
      <div class="details">
        <slot></slot>
        <div
          @fragmentInclude:replaced=${this.handleHero}
          @bookingSlot-remove=${this.handleBookingSlotRemove}
          @bookingSlot-updated=${this.handleBookingSlotUpdate}
          class="reservations">
          ${this.bookingSlotsCount ? this.renderLabel() : this.renderHero()}
          <ef-grid-list col="1">
            ${keyed(
              this.fragmentKey,
              html` <ef-fragment-include
                src=${this.fragmentUrl}></ef-fragment-include>`
            )}
          </ef-grid-list>
        </div>
      </div>
    `;
  }
}

BookingCalendar.registerTagName('bu-booking-calendar');

customElements.get('bu-booking-calendar') ||
  customElements.define('bu-booking-calendar', BookingCalendar);
